import { AnyAction } from "redux";
import { addDays, format } from "date-fns";

import {
  BOOKING_AVAILABILITY_CONTACT_ENDPOINT,
  BOOKING_CANCEL_RESERVATION_ENDPOINT,
  BOOKING_CHECKOUT_ENDPOINT,
  BOOKING_CLEANING_PRODUCTS_ENDPOINT,
  BOOKING_PAYMENT_METHODS_ENDPOINT,
  BOOKING_PLANS_ENDPOINT,
  BOOKING_PLANS_SERVICE_VOUCHER_ENDPOINT,
  BOOKING_PREVIOUS_AGENTS_ENDPOINT,
  BOOKING_PRICING_ENDPOINT,
  BOOKING_RESERVATION_ENDPOINT,
  BOOKING_VOUCHER_ENDPOINT,
  CLIENT_REGISTRATION_ENDPOINT,
  LOCATIONS_ENDPOINT,
  BOOKING_URSSAF_PRICING_ENDPOINT,
  BOOKING_FREQUENCIES_ENDPOINT,
  BOOKING_AVAILAVILITY_WEEKLY_ENDPOINT,
  BOOKING_AVAILAVILITY_CUSTOM_ENDPOINT,
} from "@config/endpoints";
import { FeatureTogglesType } from "@config/featureToggles";

import { PAYMENT_METHODS } from "@typings/globals";
import {
  AGENT_PREFERENCE,
  AGENTS_FILTER,
  CLEANING_FREQUENCY,
  KEY_PICK,
  Task,
  NonStandardFrequencyShifts,
  PetsDetails,
} from "@typings/booking";
import {
  ThunkActionOptions,
  ThunkActionDispatch,
} from "@typings/reduxThunkActions";
import { VoucherInfo } from "@typings/bookingsToValidate";
import { JobRaw } from "@typings/jobs";
import {
  AvailabilitiesCustomRequest,
  AvailabilitiesWeeklyRequest,
} from "@typings/bookingAvailabilities";
import {
  ActionParams,
  FetchCustomProps,
  FetchWeeklyProps,
} from "@typings/bookingCalendarV3";

import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { ReduxActionNameCreator } from "@services/ReduxActionNameCreator";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { buildUrl } from "@services/BuildUrl";
import { DATE_FORMAT } from "@services/DateFormatter";

import { ContactUsForBookingFormData } from "@app/pages/Booking/modals/ContactUsForBooking.helpers";
import { asyncActionCreator } from "@app/services/AsyncActionCreator";
import { IStore } from "@app/redux/reducers";
import {
  abortControllerHelper,
  deleteLocalStorageHelper,
  setFromLocalStorageHelper,
  setLocalStorageHelper,
} from "@app/redux/helpers";
import { genericPendingAction } from "@app/services/GenericPendingAction";

import {
  getHasUrssaf,
  getNextPeriodStartDate,
  getIsTouchedCalendar,
  getSelectedCleaningFrequency,
  getExecutionDate,
  getExecution,
} from "./booking.selectors";
import {
  getAddLocationEndpointRequest,
  getAvailabilitiesBodyRequest,
  getCheckoutEndpointRequest,
  getClientRegistrationEndpointRequest,
  getPricingEndpointRequest,
  getReservationEndpointRequest,
  getSendBookingContactFormRequest,
} from "./booking.repository";
import {
  BookingRegistrationForm,
  LOCAL_STORAGE_KEY,
  CheckoutParams,
  BookingReducer,
} from "./booking.typings";

const action = new ReduxActionNameCreator("booking");

const abortControllers = {
  fetchPricings: abortControllerHelper(),
  fetchBookingAvailability: abortControllerHelper(),
  fetchCustomBookingAvailability: abortControllerHelper(),
  fetchBookingWeeklyAvailabilities: abortControllerHelper(),
  fetchBookingCustomAvailabilities: abortControllerHelper(),
  fetchPlans: abortControllerHelper(),
  fetchServiceVoucherPlans: abortControllerHelper(),
  fetchPreviousAgents: abortControllerHelper(),
  fetchVoucher: abortControllerHelper(),
  fetchPaymentMethods: abortControllerHelper(),
  fetchCleaningProducts: abortControllerHelper(),
};

export const BOOKING_ACTIONS = {
  FETCH_AVAILABILITIES: action.make("fetch weekly availabilities v3"),
  FETCH_PRICINGS: action.make("fetch pricings"),
  SET_PRICE_WILL_CHANGE: action.make("set price will change"),
  FETCH_PLANS: action.make("fetch plans"),
  MAKE_RESERVATION: action.make("make reservation"),
  SET_AGENT_PREFERENCE: action.make("set agent preference"),
  SET_EXECUTION: action.make("set execution"),
  SET_FREQUENCY: action.make("set frequency"),
  SET_PRICE_PLAN: action.make("set price plan"),
  FETCH_PREVIOUS_AGENTS: action.make("fetch previous agents"),
  SET_TASK: action.make("set task"),
  RESET_TASKS: action.make("reset tasks"),
  SET_SERVICE_LOCATION: action.make("set service location"),
  FETCH_VOUCHER: action.make("fetch voucher"),
  RESET_VOUCHER: action.make("reset voucher"),
  SET_VOUCHER: action.make("set voucher"),
  SET_SPECIAL_INSTRUCTIONS: action.make("set special instructions"),
  SET_KEY_PICK: action.make("set key pick"),
  SET_LOCATION_COMMENT: action.make("set location comment"),
  SET_LOCATION_UUID: action.make("set location uuid"),
  SET_PRESELECTED_AGENT_UUID: action.make("set preselected agent uuid"),
  SET_PETS: action.make("set pets"),
  SET_REGISTRATION_FORM: action.make("set registration form"),
  REGISTER_CLIENT: action.make("register client"),
  CANCEL_RESERVATION: action.make("cancel reservation"),
  ADD_LOCATION: action.make("add location"),
  FETCH_PAYMENT_METHODS: action.make("fetch payment methods"),
  SET_PAYMENT_METHOD: action.make("set payment method"),
  MAKE_CHECKOUT: action.make("make checkout"),
  SET_IS_CHOOSING_ANY_BATMAID: action.make("set is choosing any batmaid"),
  SET_IN_LOCAL_STORAGE: action.make("set in local storage"),
  SET_FROM_LOCAL_STORAGE: action.make("set from local storage"),
  DELETE_LOCAL_STORAGE: action.make("delete local storage"),
  SET_TEMPLATE_JOB_ID: action.make("set template job id"),
  SET_FROM_JOB_TEMPLATE: action.make("set from job template"),
  SET_QUERY_PARAMS: action.make("set query params"),
  LEAVE_PROCESS: action.make("leave booking process"),
  SEND_BOOKING_CONTACT_FORM: action.make("send booking contact form"),
  FETCH_CLEANING_PRODUCTS: action.make("fetch cleaning products"),
  ADD_CLEANING_PRODUCT_ID: action.make("add cleaning product id"),
  REMOVE_CLEANING_PRODUCT_ID: action.make("remove cleaning product id"),
  SET_NON_STANDARD_FREQUENCY_CLEANINGS: action.make(
    "set non standard frequency cleanings",
  ),
  SET_CONTACT_AFTER_BOOKING: action.make("set contact after booking"),
  SET_HAS_SERVICE_VOUCHERS: action.make("set has service vouchers"),
  SET_HAS_URSSAF: action.make("set has urssaf"),
  FETCH_SERVICE_VOUCHER_PLANS: action.make("fetch service voucher plans"),
  FETCH_URSSAF_PRICINGS: action.make("fetch urssaf pricings"),
  SET_TIME_SLOT_ID: action.make("set time slot id"),
  FETCH_FREQUENCIES: action.make("fetch frequencies"),
  SET_TOUCH_CALENDAR: action.make("set touch calendar"),
};

// Actions creators
export const fetchBookingWeeklyAvailabilities =
  (data: FetchWeeklyProps, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(BOOKING_ACTIONS.FETCH_AVAILABILITIES));

    const isTouched = getIsTouchedCalendar(store());
    const oldExecutionDateTime = getExecution(store());
    const startDate = data.wideSearch
      ? null
      : getNextPeriodStartDate(store(), data.changeType, isTouched);

    const body: AvailabilitiesWeeklyRequest = {
      ...getAvailabilitiesBodyRequest(store(), data.serviceLocationId),
      startDate,
    };

    const action = await asyncActionCreator(
      {
        body,
        url: BOOKING_AVAILAVILITY_WEEKLY_ENDPOINT,
        action: BOOKING_ACTIONS.FETCH_AVAILABILITIES,
        method: ASYNC_ACTION_TYPES.POST,
        params: {
          isTouched,
          oldExecutionDateTime,
          startDate,
          changeType: data.changeType,
        } as ActionParams,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchBookingWeeklyAvailabilities(),
        }),
      },
    );

    return dispatch(action);
  };

export const fetchBookingCustomAvailabilities =
  (data: FetchCustomProps, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(BOOKING_ACTIONS.FETCH_AVAILABILITIES));

    const isTouched = getIsTouchedCalendar(store());
    const oldExecutionDateTime = getExecution(store());
    const startDate = data.wideSearch
      ? null
      : getNextPeriodStartDate(store(), data.changeType, isTouched);

    const body: AvailabilitiesCustomRequest = {
      ...getAvailabilitiesBodyRequest(store(), data.serviceLocationId),
      startDate,
      numberOfDays: data.numberOfDays,
    };

    const action = await asyncActionCreator(
      {
        body,
        url: BOOKING_AVAILAVILITY_CUSTOM_ENDPOINT,
        action: BOOKING_ACTIONS.FETCH_AVAILABILITIES,
        method: ASYNC_ACTION_TYPES.POST,
        params: {
          isTouched,
          oldExecutionDateTime,
          startDate,
          changeType: data.changeType,
        } as ActionParams,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchBookingCustomAvailabilities(),
        }),
      },
    );

    return dispatch(action);
  };

export const setTask = (payload: Task): AnyAction => ({
  type: BOOKING_ACTIONS.SET_TASK,
  payload,
});

export const resetTasks = (): AnyAction => ({
  type: BOOKING_ACTIONS.RESET_TASKS,
});

export const setKeyPick = (payload: KEY_PICK): AnyAction => ({
  type: BOOKING_ACTIONS.SET_KEY_PICK,
  payload,
});

export const setLocationComment = (payload: string): AnyAction => ({
  type: BOOKING_ACTIONS.SET_LOCATION_COMMENT,
  payload,
});

export const setServiceLocation = (payload: number): AnyAction => ({
  type: BOOKING_ACTIONS.SET_SERVICE_LOCATION,
  payload,
});

export const fetchPricings =
  (
    serviceLocationId: number,
    featureToggles?: FeatureTogglesType,
    opts?: ThunkActionOptions,
  ) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(BOOKING_ACTIONS.FETCH_PRICINGS));

    const body = getPricingEndpointRequest(
      store(),
      serviceLocationId,
      featureToggles,
    );

    const action = await asyncActionCreator(
      {
        url: BOOKING_PRICING_ENDPOINT,
        action: BOOKING_ACTIONS.FETCH_PRICINGS,
        method: ASYNC_ACTION_TYPES.POST,
        body,
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchPricings() }),
      },
    );

    return dispatch(action);
  };

/**
 * This action is an exact copy of fetchPricings.
 * The backend implementation is that it shoud accept the same request and return the same response as /client/booking/pricing.
 */
export const fetchUrssafPricings =
  (
    serviceLocationId: number,
    featureToggles?: FeatureTogglesType,
    opts?: ThunkActionOptions,
  ) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getPricingEndpointRequest(
      store(),
      serviceLocationId,
      featureToggles,
    );

    const action = await asyncActionCreator(
      {
        url: BOOKING_URSSAF_PRICING_ENDPOINT,
        action: BOOKING_ACTIONS.FETCH_URSSAF_PRICINGS,
        method: ASYNC_ACTION_TYPES.POST,
        body,
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchPricings() }),
      },
    );

    return dispatch(action);
  };

export const fetchPlans =
  (serviceLocationId: number, opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(BOOKING_ACTIONS.FETCH_PLANS));

    const payByUrssaf = getHasUrssaf(store());

    const action = await asyncActionCreator(
      {
        url: buildUrl(BOOKING_PLANS_ENDPOINT)
          .setQuery("serviceLocationId", String(serviceLocationId))
          .addQuery("payByUrssaf", String(payByUrssaf), { optional: true })
          .done(),
        action: BOOKING_ACTIONS.FETCH_PLANS,
        method: ASYNC_ACTION_TYPES.GET,
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchPlans() }),
      },
    );

    return dispatch(action);
  };

export const fetchServiceVoucherPlans =
  (serviceLocationId: number, opts?: ThunkActionOptions) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(BOOKING_ACTIONS.FETCH_SERVICE_VOUCHER_PLANS));

    const action = await asyncActionCreator(
      {
        url: buildUrl(BOOKING_PLANS_SERVICE_VOUCHER_ENDPOINT)
          .setQuery("serviceLocationId", String(serviceLocationId))
          .done(),
        action: BOOKING_ACTIONS.FETCH_SERVICE_VOUCHER_PLANS,
        method: ASYNC_ACTION_TYPES.GET,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchServiceVoucherPlans(),
        }),
      },
    );

    return dispatch(action);
  };

export const fetchPreviousAgents =
  (
    serviceLocationId: number,
    filter: AGENTS_FILTER,
    opts?: ThunkActionOptions,
  ) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(BOOKING_ACTIONS.FETCH_PREVIOUS_AGENTS));

    const action = await asyncActionCreator(
      {
        url: buildUrl(BOOKING_PREVIOUS_AGENTS_ENDPOINT)
          .setQuery("filter", filter)
          .setQuery("serviceLocationId", String(serviceLocationId))
          .done(),
        action: BOOKING_ACTIONS.FETCH_PREVIOUS_AGENTS,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchPreviousAgents(),
        }),
      },
    );

    return dispatch(action);
  };

export const setAgentPreference = (
  agentPreference: AGENT_PREFERENCE,
): AnyAction => ({
  type: BOOKING_ACTIONS.SET_AGENT_PREFERENCE,
  payload: {
    agentPreference,
  },
});

export const setPreselectedAgentUuid = (
  preselectedAgentUuid: string,
): AnyAction => ({
  type: BOOKING_ACTIONS.SET_PRESELECTED_AGENT_UUID,
  payload: {
    preselectedAgentUuid,
  },
});

export const setFrequency =
  (frequency: CLEANING_FREQUENCY, featureToggles?: FeatureTogglesType) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AnyAction> => {
    const serviceLocationId = store().booking.get("serviceLocationId");

    const action: AnyAction = {
      type: BOOKING_ACTIONS.SET_FREQUENCY,
      payload: {
        frequency,
        serviceLocationId,
      },
      params: {
        featureToggles,
      },
    };

    return dispatch(action);
  };

export const setPricePlan = (pricePlanUuid: string | null): AnyAction => ({
  type: BOOKING_ACTIONS.SET_PRICE_PLAN,
  payload: {
    pricePlanUuid,
  },
});

export const setExecution = (
  payload: Partial<BookingReducer["execution"]>,
): AnyAction => ({
  type: BOOKING_ACTIONS.SET_EXECUTION,
  payload,
});

interface MakeReservationCustomData {
  timeslotId?: string;
}

export const makeReservation =
  (
    featureToggles?: FeatureTogglesType,
    customData: MakeReservationCustomData = {},
  ) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getReservationEndpointRequest(store(), featureToggles);

    const action = await asyncActionCreator({
      url: BOOKING_RESERVATION_ENDPOINT,
      action: BOOKING_ACTIONS.MAKE_RESERVATION,
      method: ASYNC_ACTION_TYPES.POST,
      body: {
        ...body,
        ...customData,
      },
    });

    return dispatch(action);
  };

export const setSpecialInstructions = (payload: string): AnyAction => ({
  type: BOOKING_ACTIONS.SET_SPECIAL_INSTRUCTIONS,
  payload,
});

export const fetchVoucher =
  (
    { token, pricePlanUuid }: { token: string; pricePlanUuid?: string | null },
    opts?: ThunkActionOptions,
  ) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const frequency = getSelectedCleaningFrequency(store());
    const executionDate = getExecutionDate(store());

    const fallbackExecutionDate = addDays(new Date(), 2);
    const date = executionDate
      ? new Date(executionDate)
      : fallbackExecutionDate;
    const formattedDate = format(
      date,
      DATE_FORMAT.YEAR_MONTH_DAY_HOUR_MINUTE_SECOND_ISO_8601,
    );

    const action = await asyncActionCreator(
      {
        url: buildUrl(BOOKING_VOUCHER_ENDPOINT)
          .setQuery("voucherToken", token)
          .setQuery("firstJobExecutionDate", formattedDate)
          .setQuery("frequency", frequency)
          .setQuery("pricePlanUuid", pricePlanUuid || "", { optional: true })
          .done(),
        action: BOOKING_ACTIONS.FETCH_VOUCHER,
        params: {
          token,
        },
      },
      {
        ...(opts?.abortEnabled && { signal: abortControllers.fetchVoucher() }),
      },
    );

    return dispatch(action);
  };

export const resetVoucher = (): AnyAction => ({
  type: BOOKING_ACTIONS.RESET_VOUCHER,
});

export const setVoucher = (payload: VoucherInfo): AnyAction => ({
  type: BOOKING_ACTIONS.SET_VOUCHER,
  payload,
});

export const setLocationUuid = (payload: string): AnyAction => ({
  type: BOOKING_ACTIONS.SET_LOCATION_UUID,
  payload,
});

export const setPets = (
  checked: boolean,
  details?: PetsDetails,
): AnyAction => ({
  type: BOOKING_ACTIONS.SET_PETS,
  payload: {
    checked,
    ...details,
  },
});

export const setTemplateJobId = (payload: string): AnyAction => ({
  type: BOOKING_ACTIONS.SET_TEMPLATE_JOB_ID,
  payload,
});

export const setFromJobTemplate = (payload: JobRaw): AnyAction => ({
  type: BOOKING_ACTIONS.SET_FROM_JOB_TEMPLATE,
  payload,
});

export const setRegistrationForm = (
  payload: Partial<BookingRegistrationForm>,
): AnyAction => ({
  type: BOOKING_ACTIONS.SET_REGISTRATION_FORM,
  payload,
});

export const registerClient =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getClientRegistrationEndpointRequest(store());

    const action = await asyncActionCreator({
      url: CLIENT_REGISTRATION_ENDPOINT,
      action: BOOKING_ACTIONS.REGISTER_CLIENT,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const cancelReservation =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: BOOKING_CANCEL_RESERVATION_ENDPOINT.replace(
        "{reservationUuid}",
        store().booking.get("reservationUuid"),
      ),
      action: BOOKING_ACTIONS.CANCEL_RESERVATION,
      method: ASYNC_ACTION_TYPES.DELETE,
    });

    return dispatch(action);
  };

export const addLocation =
  () =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getAddLocationEndpointRequest(store());

    const action = await asyncActionCreator({
      url: LOCATIONS_ENDPOINT,
      action: BOOKING_ACTIONS.ADD_LOCATION,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const fetchPaymentMethods =
  (opts?: ThunkActionOptions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(BOOKING_ACTIONS.FETCH_PAYMENT_METHODS));

    const baseURL = buildUrl(BOOKING_PAYMENT_METHODS_ENDPOINT)
      .setPath("reservationUuid", store().booking.get("reservationUuid"))
      .setQuery(
        "displayedPrice",
        store().booking.getIn(["pricing", "displayedPriceForFirstBooking"]),
      );

    const action = await asyncActionCreator(
      {
        url: baseURL.done(),
        action: BOOKING_ACTIONS.FETCH_PAYMENT_METHODS,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchPaymentMethods(),
        }),
      },
    );

    return dispatch(action);
  };

export const setPaymentMethod = (
  payload: PAYMENT_METHODS | null,
): AnyAction => ({
  type: BOOKING_ACTIONS.SET_PAYMENT_METHOD,
  payload,
});

export const checkout =
  (params?: CheckoutParams) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getCheckoutEndpointRequest(store(), { ...params });

    const action = await asyncActionCreator(
      {
        url: BOOKING_CHECKOUT_ENDPOINT.replace(
          "{reservationUuid}",
          store().booking.get("reservationUuid"),
        ),
        action: BOOKING_ACTIONS.MAKE_CHECKOUT,
        method: ASYNC_ACTION_TYPES.POST,
        body,
      },
      undefined,
      {
        paymentMethod: body.paymentMethod,
        keyPick: body.keyPick,
        locationUuid: body.locationUuid,
        returnUrl: body.returnUrl,
      },
    );

    return dispatch(action);
  };

export const setIsChoosingAnyBatmaid = (payload: boolean): AnyAction => ({
  type: BOOKING_ACTIONS.SET_IS_CHOOSING_ANY_BATMAID,
  payload,
});

export const setNonStandardFrequencyShifts = (
  payload: NonStandardFrequencyShifts[],
): AnyAction => ({
  type: BOOKING_ACTIONS.SET_NON_STANDARD_FREQUENCY_CLEANINGS,
  payload,
});

export const setLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch, store: () => IStore): void => {
    setLocalStorageHelper(
      dispatch,
      BOOKING_ACTIONS.SET_IN_LOCAL_STORAGE,
      store().booking,
      LOCAL_STORAGE_KEY,
    );
  };

export const setFromLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    setFromLocalStorageHelper(
      dispatch,
      BOOKING_ACTIONS.SET_FROM_LOCAL_STORAGE,
      LOCAL_STORAGE_KEY,
    );
  };

export const deleteLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    deleteLocalStorageHelper(
      dispatch,
      BOOKING_ACTIONS.DELETE_LOCAL_STORAGE,
      LOCAL_STORAGE_KEY,
    );
  };

export const setQueryParams = (payload: string): AnyAction => ({
  type: BOOKING_ACTIONS.SET_QUERY_PARAMS,
  payload,
});

export const leaveProcess = (): AnyAction => ({
  type: BOOKING_ACTIONS.LEAVE_PROCESS,
});

export const sendBookingContactForm =
  (formBody: ContactUsForBookingFormData) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const body = getSendBookingContactFormRequest(store(), formBody);

    const action = await asyncActionCreator({
      action: BOOKING_ACTIONS.SEND_BOOKING_CONTACT_FORM,
      url: BOOKING_AVAILABILITY_CONTACT_ENDPOINT,
      method: ASYNC_ACTION_TYPES.POST,
      body,
    });

    return dispatch(action);
  };

export const fetchCleaningProducts =
  (opts?: ThunkActionOptions) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator(
      {
        url: BOOKING_CLEANING_PRODUCTS_ENDPOINT,
        action: BOOKING_ACTIONS.FETCH_CLEANING_PRODUCTS,
        method: ASYNC_ACTION_TYPES.GET,
      },
      {
        ...(opts?.abortEnabled && {
          signal: abortControllers.fetchCleaningProducts(),
        }),
      },
    );

    return dispatch(action);
  };

export const addCleaningProductId = (payload: string): AnyAction => ({
  type: BOOKING_ACTIONS.ADD_CLEANING_PRODUCT_ID,
  payload,
});

export const removeCleaningProductId = (payload: string): AnyAction => ({
  type: BOOKING_ACTIONS.REMOVE_CLEANING_PRODUCT_ID,
  payload,
});

export const setContactAfterBooking = (payload: boolean): AnyAction => ({
  type: BOOKING_ACTIONS.SET_CONTACT_AFTER_BOOKING,
  payload,
});

export const setHasServiceVouchers = (payload: boolean): AnyAction => ({
  type: BOOKING_ACTIONS.SET_HAS_SERVICE_VOUCHERS,
  payload,
});

export const setHasUrssaf = (payload: boolean): AnyAction => ({
  type: BOOKING_ACTIONS.SET_HAS_URSSAF,
  payload,
});

export const setTimeSlotId = (
  payload: BookingReducer["selectedTimeSlotId"],
): AnyAction => ({
  type: BOOKING_ACTIONS.SET_TIME_SLOT_ID,
  payload,
});

export const fetchFrequencies =
  (serviceLocationId: number) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(BOOKING_ACTIONS.FETCH_FREQUENCIES));

    const action = await asyncActionCreator({
      url: buildUrl(BOOKING_FREQUENCIES_ENDPOINT)
        .setQuery("serviceLocationId", String(serviceLocationId))
        .done(),
      action: BOOKING_ACTIONS.FETCH_FREQUENCIES,
      method: ASYNC_ACTION_TYPES.GET,
    });

    return dispatch(action);
  };

export const setPriceWillChange = (payload: boolean): AnyAction => ({
  type: BOOKING_ACTIONS.SET_PRICE_WILL_CHANGE,
  payload,
});

export const setIsTouchedCalendar = (payload: boolean): AnyAction => ({
  type: BOOKING_ACTIONS.SET_TOUCH_CALENDAR,
  payload,
});
